import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Coins, Sparkles, Crown, Zap, Gift, ArrowLeft, Loader2, AlertCircle, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { Alert, AlertDescription } from '@/components/ui/alert';
import ChildSpendingLimitBanner from '@/components/points/ChildSpendingLimitBanner';
import { 
  getChildSpendingStatus, 
  isAgeGroupUnder13, 
  canChildPurchase,
  ChildSpendingStatus 
} from '@/services/childSpendingService';

interface PointsPackage {
  id: string;
  name: string;
  price_cents: number;
  points_amount: number;
  bonus_points: number;
  is_active: boolean;
}

const BuyPoints = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [packages, setPackages] = useState<PointsPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [spendingStatus, setSpendingStatus] = useState<ChildSpendingStatus | null>(null);
  const [isUnder13, setIsUnder13] = useState(false);

  const user = supabaseUser || internalUser;
  const isInternalUser = !!internalUser && !supabaseUser;

  // Determine if user is under 13
  useEffect(() => {
    const checkAgeGroup = async () => {
      if (!user) return;

      // Check age group from user data
      const ageGroup = isInternalUser 
        ? internalUser?.age_group 
        : profile?.age_group;

      const under13 = isAgeGroupUnder13(ageGroup);
      setIsUnder13(under13);

      // If under 13, fetch spending status
      if (under13) {
        const status = await getChildSpendingStatus(
          user.id, 
          isInternalUser ? 'internal' : 'supabase'
        );
        setSpendingStatus(status);
      }
    };

    checkAgeGroup();
  }, [user, isInternalUser, internalUser, profile]);

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      const { data, error } = await supabase
        .from('points_packages')
        .select('*')
        .eq('is_active', true)
        .order('price_cents', { ascending: true });

      if (error) throw error;
      setPackages(data || []);
    } catch (error) {
      console.error('Error loading packages:', error);
      toast.error('Failed to load point packages');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (pkg: PointsPackage) => {
    if (!user) {
      toast.error('Please log in to purchase points');
      navigate('/login');
      return;
    }

    // Check spending limit for under-13 users
    if (isUnder13) {
      const checkResult = await canChildPurchase(
        user.id,
        isInternalUser ? 'internal' : 'supabase',
        pkg.price_cents
      );

      if (!checkResult.allowed) {
        toast.error(checkResult.reason || 'This purchase would exceed your spending limit');
        return;
      }
    }

    setPurchasing(pkg.id);
    
    try {
      const { data, error } = await supabase.functions.invoke('purchase-points', {
        body: {
          packageId: pkg.id,
          userId: user.id,
          userType: isInternalUser ? 'internal' : 'supabase'
        }
      });

      if (error) throw error;

      if (data?.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else if (data?.message) {
        // Stripe not configured yet
        toast.info(data.message);
      }
    } catch (error: any) {
      console.error('Purchase error:', error);
      toast.error(error.message || 'Failed to start purchase');
    } finally {
      setPurchasing(null);
    }
  };

  const getPackageIcon = (index: number) => {
    const icons = [
      <Coins className="h-8 w-8 text-amber-400" />,
      <Zap className="h-8 w-8 text-blue-400" />,
      <Sparkles className="h-8 w-8 text-purple-400" />,
      <Crown className="h-8 w-8 text-yellow-400" />
    ];
    return icons[index] || icons[0];
  };

  const getPackageColor = (index: number) => {
    const colors = [
      'border-amber-500/30 hover:border-amber-500/50',
      'border-blue-500/30 hover:border-blue-500/50',
      'border-purple-500/30 hover:border-purple-500/50',
      'border-yellow-500/30 hover:border-yellow-500/50 ring-2 ring-yellow-500/20'
    ];
    return colors[index] || colors[0];
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Check if a package exceeds remaining spending limit
  const isPackageDisabled = (pkg: PointsPackage): boolean => {
    if (!isUnder13 || !spendingStatus) return false;
    return pkg.price_cents > spendingStatus.remainingCents;
  };

  const handleAskParent = () => {
    // Navigate to a contact parent page or show modal
    toast.info('Ask your parent to visit the "Buy Points" page and gift you points!');
  };

  if (!user) {
    return null;
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {isMobile ? <MobileHeader /> : <Header />}
      
      <main className={`flex-grow w-full ${
        isMobile 
          ? 'pt-16 mb-16 py-6 px-4' 
          : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'
      }`}>
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/points')}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Points
          </Button>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Buy Points
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Purchase points to unlock premium features, gift to teammates, and climb the leaderboard faster!
            </p>
          </div>

          {/* Under-13 Spending Limit Banner */}
          {isUnder13 && spendingStatus && (
            <ChildSpendingLimitBanner
              currentSpentCents={spendingStatus.currentSpentCents}
              limitCents={spendingStatus.limitCents}
              remainingCents={spendingStatus.remainingCents}
              percentageUsed={spendingStatus.percentageUsed}
              isAtLimit={spendingStatus.isAtLimit}
              onAskParentClick={handleAskParent}
            />
          )}

          {/* Gifting Info Banner */}
          <Alert className="bg-primary/10 border-primary/30">
            <Gift className="h-5 w-5 text-primary" />
            <AlertDescription className="text-foreground">
              <strong>Coach, Team Owner, or Venue?</strong> Purchased points can be gifted to any user! 
              Support your players and reward outstanding performance.
              {isUnder13 && (
                <span className="block mt-1 text-sm text-muted-foreground">
                  <strong>Parents:</strong> You can purchase points here and gift them to your child through the Parent Dashboard.
                </span>
              )}
            </AlertDescription>
          </Alert>

          {/* Packages Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : packages.length === 0 ? (
            <Alert className="bg-yellow-500/10 border-yellow-500/30">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              <AlertDescription className="text-foreground">
                Point packages are being set up. Please check back soon!
              </AlertDescription>
            </Alert>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {packages.map((pkg, index) => {
                const disabled = isPackageDisabled(pkg);
                const atLimit = isUnder13 && spendingStatus?.isAtLimit;
                
                return (
                  <Card 
                    key={pkg.id} 
                    className={`bg-card border-2 transition-all duration-300 ${getPackageColor(index)} relative overflow-hidden ${disabled ? 'opacity-60' : ''}`}
                  >
                    {index === packages.length - 1 && !disabled && (
                      <Badge className="absolute top-3 right-3 bg-yellow-500 text-black">
                        Best Value
                      </Badge>
                    )}
                    {disabled && (
                      <Badge className="absolute top-3 right-3 bg-destructive text-destructive-foreground">
                        <Ban className="h-3 w-3 mr-1" />
                        Exceeds Limit
                      </Badge>
                    )}
                    <CardHeader className="text-center pb-2">
                      <div className="mx-auto mb-3">
                        {getPackageIcon(index)}
                      </div>
                      <CardTitle className="text-xl text-foreground">{pkg.name}</CardTitle>
                      <CardDescription className="text-muted-foreground">
                        {pkg.points_amount.toLocaleString()} points
                        {pkg.bonus_points > 0 && (
                          <span className="text-primary font-semibold">
                            {' '}+ {pkg.bonus_points.toLocaleString()} bonus!
                          </span>
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center space-y-4">
                      <div className="text-3xl font-bold text-foreground">
                        {formatPrice(pkg.price_cents)}
                      </div>
                      {pkg.bonus_points > 0 && (
                        <div className="text-sm text-muted-foreground">
                          Total: {(pkg.points_amount + pkg.bonus_points).toLocaleString()} points
                        </div>
                      )}
                      <Button
                        onClick={() => handlePurchase(pkg)}
                        disabled={purchasing === pkg.id || disabled || atLimit}
                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                        title={disabled ? 'This package exceeds your remaining spending limit' : undefined}
                      >
                        {purchasing === pkg.id ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : atLimit ? (
                          'Limit Reached'
                        ) : disabled ? (
                          'Exceeds Limit'
                        ) : (
                          'Buy Now'
                        )}
                      </Button>
                      {disabled && !atLimit && spendingStatus && (
                        <p className="text-xs text-muted-foreground">
                          You have {formatPrice(spendingStatus.remainingCents)} remaining
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Info Section */}
          <Card className="bg-card border-border mt-8">
            <CardHeader>
              <CardTitle className="text-foreground">Why Buy Points?</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <Sparkles className="h-8 w-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Premium Features</h3>
                <p className="text-sm text-muted-foreground">
                  Access spotlight features and premium content
                </p>
              </div>
              <div className="text-center">
                <Gift className="h-8 w-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Gift to Others</h3>
                <p className="text-sm text-muted-foreground">
                  Coaches can gift purchased points to players
                </p>
              </div>
              <div className="text-center">
                <Crown className="h-8 w-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Climb Ranks</h3>
                <p className="text-sm text-muted-foreground">
                  Level up faster and reach Diamond status
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
      {!isMobile && <Footer />}
      <MobileBottomNav />
    </div>
  );
};

export default BuyPoints;
